import {
  detectAccountType,
  matchAccountTypeToTransferRefundType,
  resetAccountNumberAndCodeIfNeed,
  setAccountNumberAndCodeByType,
} from "@edaz/utils/bankAccount";
import { attachAutomaticDirtyWatcher, isDirty } from "@frui.ts/dirtycheck";
import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { validateAll } from "@frui.ts/validation";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import type { EntityNames } from "config";
import { Modules } from "config";
import BankCodeType from "entities/bankCodeType";
import ChangedChargeProperty from "entities/changedChargeProperty";
import type ChargeChangeOrigin from "entities/chargeChangeOrigin";
import { RefundPaymentInformationValidationRulesFactory } from "entities/chargeRefund";
import {
  DomesticCounterpartyBankValidationRules,
  ForeignCounterpartyBankValidationRulesFactory,
  SepaCounterpartyBankValidationRules,
} from "entities/counterpartyBank";
import type CustomerRefundCandidateDetailResponse from "entities/customerRefundCandidateDetailResponse";
import CustomerRefundCandidateRefundPaymentResponse from "entities/customerRefundCandidateRefundPaymentResponse";
import type CustomerRefundCandidateTransferPaymentResponse from "entities/customerRefundCandidateTransferPaymentResponse";
import CustomerRefundCandidateUpdateRequest from "entities/customerRefundCandidateUpdateRequest";
import FeeType from "entities/feeType";
import RefundPaymentType from "entities/refundPaymentType";
import type { interfaces } from "inversify";
import { computed, reaction } from "mobx";
import CustomerRefundCandidatesRepository from "repositories/customerRefundCandidatesRepository";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import EventBus, { customerRefundCandidateExported, customerRefundCandidateRejected } from "services/eventBus";
import LocalizationService from "services/localizationService";
import ModalService from "services/modalService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import { serializeAccountNumber } from "utils/filters";
import { refundPaymentTypeToTransferRefundType, transferRefundTypeToRefundPaymentType } from "utils/finance";
import AlertsViewModel from "viewModels/alertsViewModel";
import CountrySelectViewModel from "../../../countrySelectViewModel";
import EditableDetailViewModel from "../../../editableDetailViewModel";
import type PagingManagerViewModel from "../../../pagingManagerViewModel";
import type { IWithPrevNextNavigationViewModel } from "../../../types";
import CandidateRejectionViewModel from "./candidateRejectionViewModel";
import EnumRepository from "../../../../repositories/enumRepository";
import PriceListService from "services/priceListService";
import { emptyValue } from "utils/emptyValue";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

export default class CustomerRefundCandidateDetailViewModel
  extends EditableDetailViewModel<CustomerRefundCandidateDetailResponse>
  implements IWithPrevNextNavigationViewModel
{
  private unsubscriptions: Array<() => void> = [];
  readonly modelName = "customerRefundCandidate";
  readonly countrySelectViewModel: CountrySelectViewModel;
  protected closeOnSave = false;

  @computed
  get isPaymentEditable() {
    return this.item.permittedActions.paymentEditing.isAllowed && this.isUserAllowedToEdit;
  }

  @computed
  get isSEPAPayment() {
    return this.item.refundPayment.paymentType === RefundPaymentType.SepaTransfer;
  }

  @computed
  get showSEPAWarning() {
    const countryId = this.item.refundPayment.counterpartyBank.countryId ?? emptyValue;
    const isSepaMember = this.enumService.value("countries", countryId)?.isSepaMember ?? false;

    return this.item.refundPayment.paymentType === RefundPaymentType.ForeignTransfer && isSepaMember && this.isPaymentEditable;
  }

  @computed
  get originalPayment() {
    if (this.item.originalPayGatePayment !== undefined) {
      return this.item.originalPayGatePayment;
    } else {
      // One of these attributes is always filled
      return this.item.originalTransferPayment as any as CustomerRefundCandidateTransferPaymentResponse;
    }
  }

  @computed
  get isPayGateRefund() {
    return this.item.refundPayment.paymentType === RefundPaymentType.PayGate;
  }

  @computed
  get isUserAllowedToEdit() {
    return this.securityService.allowTo("finance.factualauditing", "write");
  }

  @computed
  get name() {
    return this.translate("detail");
  }

  @computed
  get hasNext() {
    return this.pagingManager.hasNext;
  }

  @computed
  get hasPrevious() {
    return this.pagingManager.hasPrevious;
  }

  @computed
  get chargeRefundFee() {
    return (this.item.fees ?? []).find(fee => fee.feeType === FeeType.ChargeRefundFee)?.amount ?? 0;
  }

  @computed
  get bankFees() {
    return (this.item.fees ?? []).reduce((sum: number, fee) => {
      if (fee.feeType === FeeType.ForeignPaymentCost || fee.feeType === FeeType.ForeignPaymentToSepaCountriesCost) {
        sum += fee.amount;
      }
      return sum;
    }, 0);
  }

  @computed
  get transferTypeOptions() {
    return this.optionsService.internalEnumOptions(RefundPaymentType).filter(item => item.id !== RefundPaymentType.PayGate);
  }

  @computed
  get bankCodeOptions() {
    return this.optionsService.internalEnumOptions(BankCodeType);
  }

  @computed
  get hasUnsavedChanges() {
    return (
      isDirty(this.item) ||
      isDirty(this.item.refundPayment) ||
      isDirty(this.item.refundPayment?.counterpartyBank) ||
      isDirty(this.item.refundPayment?.counterpartyBank?.bankAccount)
    );
  }

  constructor(
    protected itemId: string,
    private pagingManager: PagingManagerViewModel,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    private enumService: EnumService,
    private confirmationService: ConfirmationService,
    private modalService: ModalService,
    private optionsService: OptionsService,
    private repository: CustomerRefundCandidatesRepository,
    public alertsViewModel: AlertsViewModel,
    private enumRepository: EnumRepository,
    private priceListService: PriceListService,
    private candidateRejectionViewModelFactory: ReturnType<typeof CandidateRejectionViewModel.Factory>,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(itemId);
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.unsubscriptions.push(
      this.eventBus.subscribe(customerRefundCandidateRejected, this.onCandidateRejected),
      reaction(
        () => this.item.refundPayment.paymentType,
        () => this.attachValidators(this.item.refundPayment)
      )
    );
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  protected loadDetail() {
    return this.repository.get(this.itemId);
  }

  protected setItem(item: CustomerRefundCandidateDetailResponse) {
    this.attachValidators(item.refundPayment);

    attachAutomaticDirtyWatcher(item);
    attachAutomaticDirtyWatcher(item.refundPayment);
    if (item.refundPayment.counterpartyBank) {
      attachAutomaticDirtyWatcher(item.refundPayment?.counterpartyBank);
    }
    if (item.refundPayment.counterpartyBank?.bankAccount) {
      attachAutomaticDirtyWatcher(item.refundPayment?.counterpartyBank.bankAccount);
    }

    super.setItem(item);
  }

  @bound
  @watchBusy
  async onNextClick() {
    const nextItemId = await this.pagingManager.onNextClick();

    if (nextItemId) {
      this.itemId = nextItemId;
      this.navigationName = `${nextItemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  @bound
  @watchBusy
  async onPreviousClick() {
    const prevItemId = await this.pagingManager.onPreviousClick();

    if (prevItemId) {
      this.itemId = prevItemId;
      this.navigationName = `${prevItemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  @bound
  @watchBusy
  async onCancelTransferRefundModificationClick() {
    await this.onInitialize();
  }

  protected validate(): boolean {
    if (this.item.refundPayment.paymentType === RefundPaymentType.PayGate) {
      return true;
    } else {
      return validateAll([this.item.refundPayment, this.item.refundPayment.counterpartyBank]);
    }
  }

  @bound
  async updateData() {
    const request = new CustomerRefundCandidateUpdateRequest();
    request.isWithoutRefundFee = this.item.isWithoutRefundFee;
    if (this.item.refundPayment.paymentType !== RefundPaymentType.PayGate) {
      request.refundPayment = this.item.refundPayment;
    }

    if (request.refundPayment?.counterpartyBank) {
      const serializedAccountNumber = serializeAccountNumber(request.refundPayment?.counterpartyBank?.accountNumber);
      if (serializedAccountNumber) {
        request.refundPayment.counterpartyBank.accountNumber = serializedAccountNumber;
      }
    }

    return await this.repository.update(this.itemId, request);
  }

  @bound
  onRejectionClick() {
    this.modalService.tryActivateChild(this.candidateRejectionViewModelFactory(this.item));
  }

  @bound
  onCandidateRejected(event: ReturnType<typeof customerRefundCandidateRejected>) {
    if (event.payload.customerRefundCandidate.id === this.itemId) {
      this.requestClose();
    }
  }

  @bound
  async onGenerateRefundClick() {
    const message = this.hasUnsavedChanges
      ? "generateRefundConfirmation.messageUnsavedChanges"
      : "generateRefundConfirmation.message";

    try {
      const confirmed = await this.confirmationService.showConfirmation(this.translate(message), {
        title: this.translate("generateRefundConfirmation.title"),
        confirmButton: {
          variant: this.hasUnsavedChanges ? "danger" : "primary",
          text: this.translate("generateRefundConfirmation.confirm"),
        },
      });

      if (confirmed) {
        await this.repository.generateRefund(this.item.id);
        this.eventBus.publish(customerRefundCandidateExported({ customerRefundCandidate: this.item }));
        this.requestClose();
      }
    } catch (e) {
      // Silent catch
    }
  }

  @bound
  onInvalidAccountNumberPasted(value: string, event: ClipboardEvent) {
    const accountType = detectAccountType(value);
    const transferType = matchAccountTypeToTransferRefundType(accountType);
    const paymentType = transferRefundTypeToRefundPaymentType(transferType);

    if (paymentType !== this.item.refundPayment.paymentType) {
      this.item.refundPayment.paymentType = paymentType;
      if (this.item.refundPayment.counterpartyBank) {
        setAccountNumberAndCodeByType(value, accountType, this.item.refundPayment.counterpartyBank);
        // We need to prevent default beacuse we're setting the input value above
        event.preventDefault();
      }
    }
  }

  @bound
  private attachValidators(item: CustomerRefundCandidateRefundPaymentResponse) {
    if (item.counterpartyBank) {
      const transferType = refundPaymentTypeToTransferRefundType(item.paymentType);

      if (transferType) {
        resetAccountNumberAndCodeIfNeed(item.counterpartyBank, transferType, item.counterpartyBank.codeType);
      }
    }

    attachAutomaticValidator(item, RefundPaymentInformationValidationRulesFactory(item));

    switch (item.paymentType) {
      case RefundPaymentType.ForeignTransfer:
        attachAutomaticValidator(item.counterpartyBank, ForeignCounterpartyBankValidationRulesFactory(item.counterpartyBank));
        break;

      case RefundPaymentType.SepaTransfer:
        if (item.counterpartyBank) {
          item.counterpartyBank.codeType = BankCodeType.Bic;
        }
        attachAutomaticValidator(item.counterpartyBank, SepaCounterpartyBankValidationRules);
        break;

      case RefundPaymentType.DomesticTransfer:
        if (item.counterpartyBank) {
          item.counterpartyBank.codeType = undefined;
        }
        attachAutomaticValidator(item.counterpartyBank, DomesticCounterpartyBankValidationRules);
        break;
    }
  }

  @bound
  getPriceListPrice(priceListId: string) {
    return this.priceListService.chargePriceList.find(x => x.id === priceListId)?.price ?? 0;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `customerRefundCandidates.${code}`, size);
  }

  translateSubAttribute(model: EntityNames | string, attribute: string) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateChargeType(priceListId: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListId);
  }

  translateFuelType(priceListId: string) {
    const fuelTypeId = this.priceListService.chargePriceList.find(x => x.id === priceListId)?.fuelTypeId;
    if (!fuelTypeId) {
      return emptyValue;
    }
    return this.enumService.value("fuel_types", fuelTypeId)?.name ?? emptyValue;
  }

  translateCountryName(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? emptyValue;
  }

  translateChangedProperty(changedProperty: ChangedChargeProperty) {
    return this.localizationService.translateEnum("changedChargeProperty", changedProperty);
  }

  translateChangedValue(changedChargeProperty: ChangedChargeProperty, value?: string) {
    if (value && changedChargeProperty === ChangedChargeProperty.Country) {
      return this.translateCountryName(value);
    }

    return value;
  }

  translateChangeOrigin(changeOrigin: ChargeChangeOrigin) {
    return this.localizationService.translateEnum("chargeChangeOrigin", changeOrigin);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string, pagingManager: PagingManagerViewModel<any>) =>
      new CustomerRefundCandidateDetailViewModel(
        itemId,
        pagingManager,
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(EnumService),
        container.get(ConfirmationService),
        container.get(ModalService),
        container.get(OptionsService),
        container.get(CustomerRefundCandidatesRepository),
        container.get(AlertsViewModel),
        container.get(EnumRepository),
        container.get(PriceListService),
        container.get(CandidateRejectionViewModel.Factory),
        container.get(CountrySelectViewModel.Factory)
      );
  }
}
